import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaUser, FaWallet, FaStore, FaSignOutAlt, FaChevronDown, FaTicketAlt, FaVideo, FaSearch } from 'react-icons/fa';
import ProfileImageUpload from './ProfileImageUpload';
import SearchBar from './SearchBar';

function Header() {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const user = getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      if (user) {
        const { error } = await supabase
          .from('ponousers')
          .update({ is_online: false })
          .eq('id', user.id);

        if (error) throw error;

        localStorage.removeItem('user');
        toast.success('Logged out successfully');
        navigate('/login');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleProfileUpdate = (imageUrl: string) => {
    if (user) {
      const updatedUser = { ...user, profile_image: imageUrl };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setShowProfileUpload(false);
      window.location.reload();
    }
  };

  return (
    <header className="bg-white shadow-sm fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-black">ErotiqueX</span>
          </Link>

          {/* Search Bar - Desktop */}
          {user && (
            <div className="hidden md:block flex-1 max-w-lg mx-8">
              <SearchBar />
            </div>
          )}

          {/* Navigation */}
          <nav className="flex items-center space-x-4">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="hidden md:block text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link
                  to="/videoreel"
                  className="hidden md:block text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Videos
                </Link>

                {/* Profile Menu */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {user.profile_image ? (
                        <img 
                          src={user.profile_image} 
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-600 font-medium">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <FaChevronDown className="w-4 h-4" />
                  </button>

                  {/* Dropdown Menu */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                      <Link
                        to={`/profile/${user.id}`}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <FaUser className="w-4 h-4 mr-3" />
                        Profile
                      </Link>

                      <button
                        onClick={() => {
                          setShowProfileUpload(true);
                          setShowProfileMenu(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <FaUser className="w-4 h-4 mr-3" />
                        Update Picture
                      </button>

                      <Link
                        to="/videoreel"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <FaVideo className="w-4 h-4 mr-3" />
                        Videos
                      </Link>

                     

                      <Link
                        to="/store"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <FaStore className="w-4 h-4 mr-3" />
                        Store
                      </Link>

                      <Link
                        to="/coupons"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <FaTicketAlt className="w-4 h-4 mr-3" />
                        Coupons
                      </Link>

                      <div className="border-t border-gray-100">
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FaSignOutAlt className="w-4 h-4 mr-3" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800"
                >
                  Sign up
                </Link>
              </>
            )}
          </nav>
        </div>

        {/* Mobile Search */}
        {user && showMobileSearch && (
          <div className="md:hidden px-4 pb-4">
            <SearchBar />
          </div>
        )}
      </div>

      {/* Profile Image Upload Modal */}
      {showProfileUpload && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Update Profile Picture</h3>
              <button
                onClick={() => setShowProfileUpload(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <ProfileImageUpload
              userId={user.id}
              currentImage={user.profile_image}
              onImageUpdate={handleProfileUpdate}
            />
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;